import React, { useState } from 'react'

import { cva, type VariantProps } from 'class-variance-authority'
import {
	AlertCircleIcon,
	AlertTriangleIcon,
	CheckCircle2Icon,
	InfoIcon,
	XIcon,
} from 'lucide-react'

import { cn } from '@/shared/utils/helpers'

const alertVariants = cva(
	'relative w-full rounded-lg border px-4 py-3 text-sm [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-1/2 [&>svg]:-translate-y-1/2 [&>svg+div]:pl-7 [&>svg]:text-foreground',
	{
		variants: {
			variant: {
				default: 'bg-background text-foreground border-border',
				info: 'bg-status-info/10 text-status-info border-status-info/30 [&>svg]:text-status-info',
				success:
					'bg-status-success/10 text-status-success border-status-success/30 [&>svg]:text-status-success',
				warning:
					'bg-status-warning/10 text-status-warning border-status-warning/30 [&>svg]:text-status-warning',
				error:
					'bg-destructive/10 text-destructive border-destructive/30 [&>svg]:text-destructive',
			},
		},
		defaultVariants: {
			variant: 'default',
		},
	}
)

const variantIcons = {
	default: InfoIcon,
	info: InfoIcon,
	success: CheckCircle2Icon,
	warning: AlertTriangleIcon,
	error: AlertCircleIcon,
}

export interface AlertProps
	extends React.HTMLAttributes<HTMLDivElement>,
		VariantProps<typeof alertVariants> {
	showIcon?: boolean
	closable?: boolean
	onClose?: () => void
	icon?: React.ReactNode
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
	(
		{
			className,
			variant = 'default',
			showIcon = true,
			closable = false,
			onClose,
			icon,
			children,
			...props
		},
		ref
	) => {
		const [visible, setVisible] = useState(true)

		const handleClose = () => {
			setVisible(false)
			onClose?.()
		}

		if (!visible) return null

		const IconComponent = variantIcons[variant || 'default']

		return (
			<div
				ref={ref}
				role="alert"
				className={cn(alertVariants({ variant }), className)}
				{...props}
			>
				{showIcon && (icon || <IconComponent className="size-4" />)}
				<div className="flex-1">{children}</div>
				{closable && (
					<button
						onClick={handleClose}
						className="absolute right-4 top-1/2 -translate-y-1/2 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
						aria-label="Close alert"
					>
						<XIcon className="size-4" />
					</button>
				)}
			</div>
		)
	}
)
Alert.displayName = 'Alert'

const AlertTitle = React.forwardRef<
	HTMLParagraphElement,
	React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
	<h5
		ref={ref}
		className={cn('mb-1 font-medium leading-none tracking-tight', className)}
		{...props}
	/>
))
AlertTitle.displayName = 'AlertTitle'

const AlertDescription = React.forwardRef<
	HTMLParagraphElement,
	React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
	<div
		ref={ref}
		className={cn('text-sm [&_p]:leading-relaxed', className)}
		{...props}
	/>
))
AlertDescription.displayName = 'AlertDescription'

export { Alert, AlertTitle, AlertDescription }
